/**
 * LE PLAN DU PARC AQUATIQUE: un grand bassin, un pavillon, des fontaines, et
 * des GLISSADES qu'on rejoint par l'escalier.
 *
 * LA COMMANDE DU PROPRIETAIRE, mot pour mot: « with glissades and stairs to go
 * up and such. With a large pool, pool house, nice fountains. A big place. »
 * Tout est la, et rien de ce qui suit n'a demande un mecanisme neuf — c'est
 * exactement le propos de ce fichier.
 *
 *   - LE GRAND BASSIN est une boite de `bassins`, la donnee qui dit ou l'on
 *     nage (voir `dansPiscine`, generalise pour l'occasion). Il fait 8,6 x 6 m,
 *     soit deux fois l'emprise entiere d'une piscine du catalogue.
 *   - LE PAVILLON est une CLOISON INTERIEURE, le mecanisme des enclos du zoo et
 *     des chambres de l'hotel (`PLAN_INTERIEUR`): une boite, une porte sur sa
 *     face -z, et l'on y entre.
 *   - LES FONTAINES sont des points du plan. Elles ne repoussent personne et ne
 *     declenchent rien: c'est du decor, comme le palmier du toit de l'hotel.
 *   - LES GLISSADES partent d'un NIVEAU (la passerelle, niveau 1) et arrivent
 *     dans le bassin, au niveau 0. On y monte par l'ESCALIER, celui de la villa
 *     a etage, deduit du nombre de niveaux par `accesEtage`.
 *
 * CE QU'IL FAUT SAVOIR DE LA PASSERELLE, et c'est ecrit ici pour que personne
 * ne le decouvre en jouant. Le moteur n'a AUCUNE verticalite: un niveau est un
 * entier a cote de la position, et `murEtage` borde TOUTE l'emprise d'un
 * acrotere a chaque niveau. Le niveau 1 du parc est donc une passerelle qui
 * couvre le parc entier, pas une petite plate-forme au sommet d'une tour. C'est
 * assume, et c'est meme ce qu'un parc aquatique a de plus credible: une grande
 * structure de bois au-dessus du bassin, avec ses departs de glissades et son
 * garde-corps. On ne tombe pas de la passerelle, comme on ne tombe pas de la
 * terrasse de la villa.
 *
 * TOUS LES NOMBRES SONT DES FRACTIONS de l'emprise (18 x 10 m), tournees
 * ensuite par `tourner`: une seule table, une seule rotation. Ecrire les quatre
 * quarts de tour a la main est ce qui avait deja mis une porte du mauvais cote.
 *
 * LA REGLE QUI COMMANDE TOUS LES CHIFFRES, la meme depuis la terrasse de la
 * villa: deux bandes de murs ne doivent JAMAIS se toucher, sinon elles se
 * renvoient le bonhomme un tick sur deux — des deux cotes du fil, donc sans le
 * moindre recalage pour le signaler. Avec l'emprise de 18 x 10:
 *
 *   - au sol, l'enceinte (mur de 0,18 m) commence a pousser a 8,47 m du centre
 *     en x et 4,47 m en z (demi-emprise moins l'epaisseur moins le rayon du
 *     bonhomme); le pavillon pousse au plus jusqu'a 8,18 m et 3,65 m, donc il
 *     reste 29 cm de bande franche en largeur et 82 cm en profondeur;
 *   - a l'etage, l'acrotere (0,28 m) commence a 8,37 m et 4,37 m, et la
 *     passerelle n'a AUCUNE piece: il n'y a rien d'autre a heurter la-haut.
 *
 * ET UNE REGLE DE PLUS, propre au bassin: AUCUNE BANDE DE MURS NE DOIT MORDRE
 * L'EAU. Un nageur repousse par un mur serait sorti de l'eau par une regle
 * qu'il ne voit pas — et comme `dansPiscine` decide aussi du filet anti-blocage
 * (`debloquer`), on l'aurait vu tressauter au bord du bassin sans qu'aucun
 * message ne l'explique. Le bassin s'arrete a 8,10 m en x — 37 cm avant la bande
 * de l'enceinte — et son bord gauche reste a 1,90 m de la bande du pavillon.
 *
 * tests/unit/parc-aquatique.test.ts ne fait confiance a aucun de ces calculs:
 * il balaie toute l'emprise, aux deux niveaux et sous les quatre rotations, et
 * exige que `resoudreMurs` soit un POINT FIXE — et il verifie qu'aucun point du
 * bassin n'est deplace par un mur.
 *
 * Module PUR: uniquement des donnees.
 */
import type { BoiteFraction, Glissade, PlanModeleEtage } from '../types/monde.ts'
import type { PieceInterieure } from './zoo.ts'

/**
 * L'EMPRISE DU PARC, en metres — ET C'EST UNE TAILLE MESUREE.
 *
 * « A big place », donc grand; mais un batiment qu'on ne peut pas poser est un
 * batiment qui n'existe pas, et c'est le zoo qui a paye cette lecon (22 x 16 m:
 * zero emplacement en foret). On mesure donc AVANT d'ecrire le chiffre, sur le
 * vrai monde et ARBRES COMPRIS — la moitie que l'aeroport avait ajoutee: le
 * relief n'est pas ce qui encombre ce monde, ce sont ses 4595 noeuds de
 * ressource.
 *
 * Balayage du monde entier (pas de 5 m, quatre rotations, sac plein), a la
 * meme mesure pour tout le monde:
 *
 *   villa a etage 14 x 11 :  95 emplacements
 *   zoo           15 x 11 :  77
 *   hotel         15 x 12 :  60   <- la barre
 *   parc          16 x 12 :  53   refuse: sous l'hotel
 *   parc          17 x 11 :  58   refuse: sous l'hotel
 *   parc          17 x 12 :  48   refuse
 *   parc          18 x 12 :  44   refuse
 *   parc          18 x 10 :  65   <- retenu
 *   parc          17 x 10 :  75
 *
 * Un mot sur le PAS DU BALAYAGE, parce qu'il a failli faire mentir la mesure:
 * a 10 m de pas, on ne recolte qu'une quinzaine d'emplacements sur 1936 points
 * et les chiffres se croisent au hasard (parc 15, hotel 18). C'est un
 * echantillon, pas une mesure. A 5 m: parc 65, hotel 60. A 3 m: parc 205,
 * hotel 175. Les deux mesures serieuses disent la meme chose, la grossiere dit
 * n'importe quoi — le test balaie donc a 5 m.
 *
 * On retient 18 x 10: c'est le batiment le plus LARGE du catalogue (l'hotel
 * fait 15 m), il se pose plus souvent que l'hotel, et 180 m² est exactement la
 * surface de l'hotel — la place tient dans la largeur, la ou le monde en a. Un
 * parc plus profond (18 x 12) coutait un tiers de ses emplacements pour deux
 * metres que le plan n'utilise pas.
 */
export const TAILLE_PARC = { l: 18, p: 10 } as const

/**
 * LE PAVILLON — le « pool house » de la commande.
 *
 * Une CLOISON INTERIEURE, exactement comme une chambre de l'hotel: on y entre
 * par sa porte, qui est sur sa face -z (la convention de toutes les ouvertures
 * du projet) et donne sur l'allee d'entree. Pas de drapeau `enclos`: on n'y
 * installe pas de bete apprivoisee, c'est un vestiaire.
 *
 * En metres: 5,04 x 4 m, centre a (-5,31 ; +1,3), porte de 1,4 m — plus large
 * qu'une porte de maison parce qu'on y entre en groupe, en maillot, et qu'une
 * porte etroite au bord d'un bassin est un endroit ou l'on se coince.
 */
export const PAVILLON_PARC: PieceInterieure = {
  nom: 'Pavillon',
  x: -0.295,
  z: 0.13,
  dl: 0.14,
  dp: 0.2,
  porte: 1.4,
}

/** Les cloisons du rez-de-chaussee du parc: le pavillon, et rien d'autre. */
export const PIECES_PARC: readonly PieceInterieure[] = Object.freeze([PAVILLON_PARC])

/**
 * LE GRAND BASSIN — 8,64 x 6 m, centre a (+3,78 ; +0,4).
 *
 * C'est une boite de `bassins` (`BatimentDef.bassins`), c'est-a-dire la DONNEE
 * qui repond a « nage-t-on ici ? ». `dansPiscine` interrogeait `kind !==
 * 'piscine'`, ce qui est exactement le piege deja paye par le potager et la
 * serre — une regle ecrite en dur pour UN modele oublie toujours les suivants.
 * La piscine du catalogue passe desormais par la meme table, avec la meme
 * boite que son emprise entiere: rien ne change pour elle.
 *
 * Il occupe les deux tiers droits du parc et laisse une allee de pres de 2 m
 * devant l'entree, une autre de 1,90 m entre lui et le pavillon: on fait le
 * tour du bassin a pied sec, ce qui est ce qu'on demande a un parc.
 */
export const BASSIN_PARC: BoiteFraction = { x: 0.21, z: 0.04, dl: 0.24, dp: 0.3 }

/** Les bassins du parc. Un seul, mais la table est une LISTE: voir le bassin
 * du toit de l'hotel le jour ou l'on voudra s'y tremper. */
export const BASSINS_PARC: readonly BoiteFraction[] = Object.freeze([BASSIN_PARC])

/**
 * LES FONTAINES — « nice fountains ».
 *
 * Du DECOR: aucune ne repousse personne, aucune ne declenche rien. Elles sont
 * ici et non dans le rendu pour la raison habituelle — ce qu'on VOIT doit etre
 * ce que la simulation connait, sinon deux ecritures des memes positions
 * finissent par ne plus etre d'accord, et les tests ne peuvent verifier ni
 * qu'elles tiennent dans l'emprise ni qu'elles ne sont pas plantees dans un
 * mur.
 *
 * Trois, a trois endroits qui se lisent de loin: une a l'entree, un GRAND JET
 * au milieu de l'eau (on nage dedans, c'est tout l'interet), et une derniere
 * dans l'allee du pavillon.
 */
export interface FontaineParc {
  /** Nom affiche au joueur, accentue. */
  nom: string
  x: number
  z: number
}

export const FONTAINES_PARC: readonly FontaineParc[] = Object.freeze([
  { nom: 'Fontaine de l’entrée', x: -0.11, z: -0.36 },
  { nom: 'Grand jet', x: 0.3, z: 0.22 },
  { nom: 'Fontaine du pavillon', x: -0.085, z: 0.15 },
])

/**
 * LES TROIS GLISSADES.
 *
 * Chacune part d'un point de la PASSERELLE (niveau 1) et arrive dans le
 * BASSIN (niveau 0). Les deux extremites sont dans le plan, en fractions,
 * parce que c'est le seul moyen qu'elles restent d'accord: le rendu dessine le
 * toboggan entre ces deux points, la cible du tap se pose sur le depart, et la
 * descente se joue entre les deux (voir `cheminGlissade` et `avancerGlissade`
 * dans shared/sim/batiments.ts).
 *
 * Les departs sont a plus de deux metres l'un de l'autre et a plus de deux
 * metres de l'escalier: un tap doit designer une glissade sans hesitation, et
 * proposer « Monter » la ou le joueur voulait glisser est le genre de mensonge
 * que ce projet paie a chaque fois.
 *
 * Les arrivees sont a plus d'un metre du bord de l'eau — pas au bord: on
 * termine dans le bassin, pas sur la margelle.
 */
export const GLISSADES_PARC: readonly Glissade[] = Object.freeze([
  { nom: 'Grande glissade', depart: { x: -0.3, z: -0.2 }, arrivee: { x: 0.1, z: -0.1 } },
  { nom: 'Glissade en colimaçon', depart: { x: -0.26, z: 0.02 }, arrivee: { x: 0.16, z: 0.2 } },
  { nom: 'Toboggan des petits', depart: { x: -0.34, z: 0.24 }, arrivee: { x: 0.05, z: 0.22 } },
])

/**
 * LE PLAN DES NIVEAUX DU PARC.
 *
 * UN SEUL niveau au-dessus du sol, donc un ESCALIER — c'est `accesEtage` qui le
 * deduit du nombre de niveaux, jamais le nom du modele: deux destinations se
 * devinent d'un tap, trois demanderaient les boutons de l'ascenseur.
 *
 * AUCUNE PIECE la-haut, et c'est voulu: une passerelle de glissades est a ciel
 * ouvert, bordee par le seul acrotere. C'est aussi ce qui rend son point fixe
 * trivial — il n'y a qu'une bande de murs a l'etage, elle ne peut en croiser
 * aucune autre.
 */
export const PLAN_PARC_AQUATIQUE: PlanModeleEtage = Object.freeze({
  acces: 'escalier',
  /**
   * L'escalier est dans le coin avant gauche, a l'ecart du bassin et des
   * departs de glissades: on entre, on longe l'allee, on monte.
   */
  accesPos: { x: -0.4, z: -0.36 },
  niveaux: [[]],
  glissades: GLISSADES_PARC,
  nomsNiveaux: ['Le parc', 'La passerelle des glissades'],
})
